import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { atomWithDefault } from 'jotai/utils';
import { ReactNode, createContext } from 'react';

import { postCopyTradePortfolioCopyApi } from '@flipster/apis/v1/copy-trade';
import { CopyExecutionMode, CopyTradeStrategyMode } from '@flipster/apis/v1/copy-trade/types';
import { T, i18nBunja } from '@flipster/i18n';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getErrorMessage } from '@flipster/utils/error';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import { getUserProfileQueryKey } from '../../../profile-page/bunja';
import { copyTradingBunja } from '../../../shared/copy-trading/state';

export const CopyTradePortfolioIdContext = createContext<string>(null!);
export const CopyTradePortfolioIdScope = createScopeFromContext(CopyTradePortfolioIdContext);
export const copyTradingSettingsBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const id = bunja.use(CopyTradePortfolioIdScope);
  const { portfoliosAtom, myStatusAtom, invalidatePortfolioQueries } = bunja.use(copyTradingBunja);
  const { getT } = bunja.use(i18nBunja);
  const t = getT();
  const portfolioAtom = atom((get) => get(portfoliosAtom).find(({ portfolioId }) => portfolioId == id));
  const availableFundsAtom = atom((get) => Num(get(myStatusAtom)?.availableBalanceUsdt || 0));

  // form
  type CopyAmountUserInput = { type: 'text'; value: string } | { type: 'pct'; value: number };
  const copyAmountUserInputAtom = atom<CopyAmountUserInput>({ type: 'text', value: '' });
  function setCopyAmountText(value: string) {
    store.set(copyAmountUserInputAtom, { type: 'text', value });
  }
  function setCopyAmountPct(value: number) {
    store.set(copyAmountUserInputAtom, { type: 'pct', value });
  }
  const copyAmountValueAtom = atom<BigNumber>((get) => {
    const input = get(copyAmountUserInputAtom);
    if (input.type === 'text') return Num(input.value || 0);
    const availableFunds = get(availableFundsAtom);
    return availableFunds.times(input.value).div(100);
  });
  const copyAmountTextAtom = atom<string>((get) => {
    const input = get(copyAmountUserInputAtom);
    if (input.type === 'text') return input.value;
    const clampedAmount = get(copyAmountValueAtom);
    return numberFormatter(clampedAmount, { precision: UsdtPrecision, toFixed: true });
  });
  const copyAmountPctAtom = atom<BigNumber>((get) => {
    const input = get(copyAmountUserInputAtom);
    if (input.type === 'pct') return Num(input.value);
    const pct = get(copyAmountValueAtom).div(get(availableFundsAtom)).times(100);
    return pct.isNaN() ? Num(0) : BigNumber.min(pct, 100);
  });
  const copyModeAtom = atom<CopyTradeStrategyMode>('COPY_ALL_EXISTING');

  // Copy Execution Mode (Fixed Ratio vs Fixed Multiplier)
  const copyExecutionModeAtom = atom<CopyExecutionMode>('FIXED_RATIO');
  const fixedMultiplierRedDotKey = 'flipster.configs.v1.copyTrade.fixedMultiplierRedDot' as const;
  const fixedMultiplierRedDotBaseAtom = atomWithDefault(() => getLocalStorage(fixedMultiplierRedDotKey) ?? true);
  const fixedMultiplierRedDotAtom = atom(
    (get) => get(fixedMultiplierRedDotBaseAtom),
    (_, set) => {
      setLocalStorage(fixedMultiplierRedDotKey, false);
      set(fixedMultiplierRedDotBaseAtom, false);
    },
  );
  function setCopyExecutionMode(mode: CopyExecutionMode) {
    store.set(copyExecutionModeAtom, mode);
    if (mode === 'FIXED_MULTIPLIER') store.set(fixedMultiplierRedDotAtom);
  }

  // Copy Multiplier (for FIXED_MULTIPLIER mode)
  const copyMultiplierTextAtom = atom<string>('1.0');
  function setCopyMultiplierText(value: string) {
    store.set(copyMultiplierTextAtom, value);
  }
  const copyMultiplierValueAtom = atom<BigNumber>((get) => {
    const text = get(copyMultiplierTextAtom);
    return Num(text || 0);
  });
  const multiplierMinAtom = atom((get) => Num(get(portfolioAtom)?.multiplierMin ?? '0.01'));
  const multiplierMaxAtom = atom((get) => Num(get(portfolioAtom)?.multiplierMax ?? '100'));

  const totalStopLossAtom = atom<BigNumber>(Num(60));
  const triggerBalanceAtom = atom((get) => {
    const copyAmount = get(copyAmountValueAtom);
    if (copyAmount.eq(0)) return;
    const totalStopLoss = get(totalStopLossAtom);
    // Trigger balance = copyAmount * (100 - totalStopLoss) / 100
    return copyAmount.times(Num(100).minus(totalStopLoss)).div(100);
  });
  const estimatedPnlAtom = atom((get) => {
    const copyAmount = get(copyAmountValueAtom);
    if (copyAmount.eq(0)) return;
    const totalStopLoss = get(totalStopLossAtom);
    // Est. P&L = -copyAmount * totalStopLoss / 100
    return copyAmount.times(totalStopLoss).div(100).negated();
  });
  const maxEntrySlippageAtom = atom<BigNumber>(Num(0.5));
  const agreedTncAtom = atom<boolean>(false);

  // validation
  const copyAmountUserInputIsEmptyAtom = atom((get) => {
    const input = get(copyAmountUserInputAtom);
    return input.type === 'text' && input.value === '';
  });
  const validMaxAmountUsdtAtom = atom<BigNumber>((get) => {
    const portfolio = get(portfolioAtom);
    if (!portfolio) return Num(0);
    const maxInvestAmountUsdt = Num(portfolio.maxInvestAmountUsdt);
    const availableAmountUsdt = Num(portfolio.availableInvestmentAmountUsdt);
    return BigNumber.min(maxInvestAmountUsdt, availableAmountUsdt);
  });
  const hasMinCopyAmountErrorAtom = atom<boolean>((get) => {
    const portfolio = get(portfolioAtom);
    const inputIsEmpty = get(copyAmountUserInputIsEmptyAtom);
    if (!portfolio || inputIsEmpty) return false;
    const copyAmountValue = get(copyAmountValueAtom);
    return copyAmountValue.lt(portfolio.minInvestAmountUsdt);
  });
  const hasMaxCopyAmountWarningAtom = atom<boolean>((get) => {
    const portfolio = get(portfolioAtom);
    const inputIsEmpty = get(copyAmountUserInputIsEmptyAtom);
    if (!portfolio || inputIsEmpty) return false;
    const value = get(copyAmountValueAtom);
    const validMaxAmountUsdt = get(validMaxAmountUsdtAtom);
    return value.gt(validMaxAmountUsdt);
  });
  const copyAmountErrorMessageAtom = atom<ReactNode | undefined>((get) => {
    const hasMinError = get(hasMinCopyAmountErrorAtom);
    if (hasMinError) {
      const minAmount = get(portfolioAtom)?.minInvestAmountUsdt;
      return t('Copy.r251107.Minimum_copy_amount_is_symbol_amount', {
        symbolAmount: `${numberFormatter(minAmount)} ${UsdtCurrency}`,
      });
    }
    const hasExceedFund = get(copyAmountValueAtom).gt(get(availableFundsAtom));
    if (hasExceedFund) return t('Trade.r250401.Exceed_fund_error');
    const hasMaxWarning = get(hasMaxCopyAmountWarningAtom);
    if (hasMaxWarning) {
      const validMaxAmountUsdt = get(validMaxAmountUsdtAtom);
      return (
        <div>
          {t('Copy.r260113.Copy_limit_exceeded_Add_up_to')}{' '}
          <button onClick={() => setCopyAmountText(validMaxAmountUsdt.toString())}>
            <Formatted.Number
              value={validMaxAmountUsdt}
              precision={UsdtPrecision}
              toFixed
              css={{ textStyle: 'Text/M/Monospace/Underline', color: 'content.state.negative' }}
            />{' '}
            <span className={css({ color: 'content.state.negative' })}>{UsdtCurrency}</span>
          </button>
        </div>
      );
    }
  });

  // Multiplier validation
  const copyMultiplierInputIsEmptyAtom = atom((get) => get(copyMultiplierTextAtom) === '');
  const copyMultiplierErrorMessageAtom = atom((get) => {
    const mode = get(copyExecutionModeAtom);
    if (mode !== 'FIXED_MULTIPLIER') return;
    const inputIsEmpty = get(copyMultiplierInputIsEmptyAtom);
    if (inputIsEmpty) return;
    const value = get(copyMultiplierValueAtom);
    const min = get(multiplierMinAtom);
    const max = get(multiplierMaxAtom);
    if (!(value.lt(min) || value.gt(max))) return;
    return (
      <T
        id='Copy.r260205.Out_of_available_range_leverage_leverage'
        values={{
          leverage1: <Formatted.Number value={min} precision={2} />,
          leverage2: <Formatted.Number value={max} precision={0} />,
        }}
      />
    );
  });

  const disabledAtom = atom((get) => {
    if (!get(agreedTncAtom)) return true;
    if (get(copyAmountErrorMessageAtom)) return true;
    if (get(copyAmountValueAtom).lte(0)) return true;

    // Check multiplier validation for FIXED_MULTIPLIER mode
    const mode = get(copyExecutionModeAtom);
    if (mode === 'FIXED_MULTIPLIER') {
      if (get(copyMultiplierInputIsEmptyAtom)) return true;
      if (get(copyMultiplierErrorMessageAtom)) return true;
    }
    return false;
  });

  // submission
  const postCopyPortfolioMutationAtom = atomWithMutation((get) => ({
    mutationFn: async () => {
      const portfolio = get(portfolioAtom);
      if (!portfolio) return;

      const copyExecutionMode = get(copyExecutionModeAtom);
      const isFixedMultiplier = copyExecutionMode === 'FIXED_MULTIPLIER';

      await postCopyTradePortfolioCopyApi(portfolio.portfolioId, {
        initialFundAmountUsdt: get(copyAmountValueAtom).toString(),
        initialCopyMode: get(copyModeAtom),
        maximumLossRatioPct: get(totalStopLossAtom).toString(),
        maxEntrySlippagePct: get(maxEntrySlippageAtom).toString(),
        copyExecutionMode,
        multiplier: isFixedMultiplier ? get(copyMultiplierValueAtom).toString() : undefined,
      });
    },
  }));
  async function submitCopyPortfolio() {
    const queryClient = store.get(queryClientAtom);
    const portfolio = store.get(portfolioAtom);
    const { mutateAsync } = store.get(postCopyPortfolioMutationAtom);
    try {
      await mutateAsync();
      snackbar.success({
        message: t('Autogen_common.r251126.s_Copy_Trading_successful'),
        button: <TextButton as='a' href='/copy-trading/activity' label={t('Autogen_common.r251126.s_View_details')} />,
      });
      queryClient.invalidateQueries({ queryKey: ['getRedDotApi'] });
      queryClient.invalidateQueries({ queryKey: getUserProfileQueryKey(portfolio?.userProfileId, true) });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    } finally {
      await invalidatePortfolioQueries();
    }
  }

  return {
    portfolioAtom,
    availableFundsAtom,
    // form
    setCopyAmountText,
    setCopyAmountPct,
    copyAmountTextAtom,
    copyAmountPctAtom,
    copyModeAtom,
    // copy execution mode
    copyExecutionModeAtom,
    setCopyExecutionMode,
    fixedMultiplierRedDotAtom,
    // copy multiplier
    copyMultiplierTextAtom,
    setCopyMultiplierText,
    multiplierMinAtom,
    multiplierMaxAtom,
    // stop loss & slippage
    totalStopLossAtom,
    triggerBalanceAtom,
    estimatedPnlAtom,
    maxEntrySlippageAtom,
    agreedTncAtom,
    // validation
    copyAmountErrorMessageAtom,
    copyMultiplierErrorMessageAtom,
    disabledAtom,
    // submission
    submitCopyPortfolio,
  };
});
copyTradingSettingsBunja.debugLabel = 'copyTradingSettingsBunja';
